import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { Box, Typography, Alert, AlertTitle, Card } from "@mui/material";

import { LoadingButton } from "@mui/lab";
import UpgradeFeatures from "components/dashboard/upgrade/UpgradeFeatures";
import Pricing from "components/dashboard/upgrade/Pricing";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";

const UpgradePage = () => {
  const {
    userData,
    subscription,
    subscriptionData,
    hasActiveSubscription,
    hasLapsedSubscription,
    canStartTrial,
    hasActiveTrial,
    trialExpired,
  } = useAuth();
  const navigate = useNavigate();
  const [manageBillingLoading, setManageBillingLoading] = useState(false);

  const handleManageBilling = async () => {
    setManageBillingLoading(true);
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        role: userData?.role,
        stripeId: userData?.stripeId,
        // No flowType = regular portal with billing history, payment methods, invoices, reactivation
      });

      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert("Failed to open billing management. Please try again.");
      setManageBillingLoading(false);
    }
  };

  const getUpgradeMessage = (
    role,
    canStartTrial,
    trialExpired,
    hasLapsedSubscription
  ) => {
    // Active subscription - show current plan message
    if (hasActiveSubscription) {
      if (subscriptionData?.cancel_at_period_end) {
        return "Your subscription is scheduled to cancel. Please use Manage Billing to continue your subscription.";
      }
      if (role === "facility") {
        return "You're currently on the CareMap Plus plan. Review your subscription details below.";
      }
      return "You're currently a HealthDesk Member. Change your subscription plan below.";
    }

    // Lapsed subscription - different messaging based on status
    if (hasLapsedSubscription) {
      if (subscriptionData?.status === "past_due") {
        return "Your subscription has been suspended due to failed payment. Use the button below to manage your billing and reactivate your subscription.";
      }
      if (subscriptionData?.status === "canceled") {
        return "Your subscription has been cancelled. Choose a plan below to resubscribe.";
      }
      return "Your subscription is no longer active. Choose a plan below to resubscribe.";
    }

    // Trial-eligible patients see different messaging
    if (role === "patient" && canStartTrial) {
      return "Select the plan that matches your usage needs.";
    }

    if (role === "patient" && trialExpired) {
      return "Your trial has ended. Continue your journey with Medical SuperIntelligence by choosing a plan below.";
    }

    // Standard messaging for other cases
    if (role === "facility") {
      return "Upgrade to CareMap Plus to access all our features and supercharge your facility.";
    }
    if (role === "patient") {
      return "Upgrade to become a HealthDesk Member. Get unlimited access to Medical SuperIntelligence, your personal health assistant.";
    }
    if (role === "professional") {
      return "Upgrade to become a HealthDesk Member. Get unlimited access to Medical SuperIntelligence for healthcare professionals.";
    }
    return "Upgrade to become a HealthDesk Member to access all our features.";
  };

  const getPageTitle = (
    role,
    canStartTrial,
    trialExpired,
    hasLapsedSubscription
  ) => {
    if (hasLapsedSubscription) {
      if (subscriptionData?.status === "past_due") {
        return "Reactivate Your Subscription";
      }
      if (subscriptionData?.status === "canceled") {
        return "Choose Your Plan";
      }
      return "Resubscribe";
    }

    if (role === "patient") {
      return "Choose Your Plan";
    }

    return "Upgrade";
  };

  const shouldShowFeatures = () => {
    // Don't show features for active subscriptions
    if (hasActiveSubscription) {
      return false;
    }

    // Don't show features for past_due subscriptions (they just need to pay)
    if (hasLapsedSubscription && subscriptionData?.status === "past_due") {
      return false;
    }

    // Don't show features for trial-eligible patients (keep it simple)
    if (userData?.role === "patient" && canStartTrial) {
      return false;
    }
    return true;
  };

  const shouldShowPricing = () => {
    // For past_due subscriptions, don't show pricing - portal can handle reactivation
    if (hasLapsedSubscription && subscriptionData?.status === "past_due") {
      return false;
    }

    // For canceled/ended subscriptions, show pricing - portal can't reactivate these
    if (hasLapsedSubscription && subscriptionData?.status === "canceled") {
      return true;
    }

    return true;
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Typography
        variant="h4"
        component="h2"
        sx={{ mt: { xs: 1, sm: 5 }, mb: 2 }}
      >
        {getPageTitle(
          userData?.role,
          canStartTrial,
          trialExpired,
          hasLapsedSubscription
        )}
      </Typography>

      {hasActiveTrial && (
        <Alert severity="info" sx={{ mt: 3, mb: 3 }}>
          <AlertTitle>Upgrade Early?</AlertTitle>
          Your free trial is still active, but you can upgrade now to ensure
          uninterrupted access.
        </Alert>
      )}

      <Typography
        variant="body"
        sx={{
          display: "block",
          mt: 1,
          mb: userData?.role === "facility" && hasActiveSubscription ? 5 : 2,
        }}
      >
        {getUpgradeMessage(
          userData?.role,
          canStartTrial,
          trialExpired,
          hasLapsedSubscription
        )}
      </Typography>

      {/* Past Due Subscription - Show Manage Billing Button */}
      {hasLapsedSubscription && subscriptionData?.status === "past_due" && (
        <Card
          sx={{
            textAlign: "center",
            py: 4,
            px: 2,
            my: 4,
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
            Update Your Payment Method
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Update your payment method and reactivate your subscription to
            continue using Medical SuperIntelligence.
          </Typography>
          <LoadingButton
            loading={manageBillingLoading}
            disabled={manageBillingLoading}
            variant="contained"
            size="large"
            onClick={handleManageBilling}
            sx={{ px: 4, py: 1.5 }}
          >
            Manage Billing
          </LoadingButton>
        </Card>
      )}

      {shouldShowFeatures() && <UpgradeFeatures role={userData?.role} />}

      {shouldShowPricing() && (
        <Pricing
          uid={userData?.uid}
          role={userData?.role}
          subscription={subscription}
        />
      )}
    </Box>
  );
};

export default UpgradePage;
